/**
 * @file
 * Functions used internally by OkcApi, wrapped for external use by the
 * interface defined in OkcApi/index.js
 */

import * as Sentry from "@sentry/browser";
import queryString from "query-string";
import getAccessToken from "shared/util/getAccessToken";
import {
	isDesktop,
	isMobile,
	isLocalHost,
} from "shared/util/EnvironmentHelpers";
import isApp from "shared/util/isApp";
import { PLATFORM } from "shared/util/getPlatform";

import {
	APIError,
	AuthError,
	NetworkError,
} from "./error";
import {
	SERVER_URL,
	API_PREFIX,
	METHODS,
	DEFAULT_ERROR,
} from "./constants";

/**
 * Gets platform for inclusion in headers.
 * Mobile web / desktop need to be explicit, apps do UA parsing server-side
 * @returns {string} A string with the device platform (e.g. "DESKTOP").
 */
export function _getPlatformHeader() {
	if (isApp()) {
		return null;
	} else if (isDesktop) {
		return PLATFORM.Desktop;
	} else if (isMobile) {
		return PLATFORM.Mobile;
	}

	return PLATFORM.Unknown;
}

/**
 * Gets device screen resolution for inclusion in headers.
 * @returns {string} A string with the screen resolution (e.g. "1280x800x1").
 */
export function _getDeviceResHeader() {
	const {
		devicePixelRatio: ratio = 1,
		screen: { width = 0, height = 0 } = {},
	} = window;

	// iOS reports css pixels, Android reports screen px
	if (window.App && window.App.ios) {
		return [width, height, ratio].join("x");
	} else {
		return [width * ratio, height * ratio, ratio].join("x");
	}
}

/**
 * Takes in requested headers, adds OkC API-specific headers.
 * @param {object} headers An map of headers.
 * @param {number} version The endpoint version (gets added to headers).
 * @returns {object} An object with all the headers needed for an OkC API call.
 */
export function _buildHeaders(headers, version) {
	const baseHeaders = {};
	const platform = _getPlatformHeader();
	const accessToken = getAccessToken();

	if (platform) {
		baseHeaders["x-okcupid-platform"] = platform;
	} else {
		baseHeaders["x-okcupid-app"] = window.App.app_version;
		baseHeaders["x-okcupid-device-id"] = window.App.token;
		baseHeaders["x-okcupid-device-res"] = _getDeviceResHeader();
	}

	if (accessToken) {
		baseHeaders.Authorization = `Bearer ${accessToken}`;
	}

	if (version) {
		baseHeaders.endpoint_version = version;
	}

	// Combine the headers, using our defaults, and letting the
	// developer shoot themselves in the foot if they want to.
	return {
		...baseHeaders,
		...headers,
	};
}

/**
 * Formats a Fetch response as either JSON (preferred) or text.
 * @param {object} response Fetch call response.
 * @returns {Promise} Promise object with `ok`, `status`, and `body`.
 */
export function _formatResponse(response) {
	const status = response ? response.status : null;
	const ok = response ? response.ok : null;
	const headers = response ? response.headers : null;

	if (!response || (!response.json && !response.text)) {
		return {
			ok,
			status,
			body: {},
		};
	}

	const isJSON = headers
		&& headers.get("content-type")
		&& headers.get("content-type").indexOf("application/json") >= 0;

	if (isJSON) {
		return response.json().then((body) => {
			return {
				ok,
				status,
				body,
			};
		});
	}

	return response.text().then((text) => {
		try {
			return {
				ok,
				status,
				body: JSON.parse(text),
			};
		} catch (_) {
			return {
				ok,
				status,
				body: text,
			};
		}
	});
}

/**
 * Formats an object into query params. Undefined/null values get removed.
 * Arrays are handled (e.g. { x: [1, 2, 3] } -> "x=1&x=2&x=3").
 * Nested objects are not handled.
 * @param {object} params An object to be transformed.
 * @param {object} options An object to be transformed.
 * @returns {string} A query param string.
 */
export function _formatUrlParameters(params = {}, { withQuerySeparator = false } = {}) {
	const cleanedUp = Object.keys(params).reduce((updatedParams, key) => {
		// No need to send these over the wire in a GET.
		if (params[key] === undefined || params[key] === null) {
			return updatedParams;
		}

		return {
			...updatedParams,
			[key]: params[key],
		};
	}, {});

	const qString = queryString.stringify(cleanedUp, { arrayFormat: "none" });

	if (withQuerySeparator) {
		return qString ? `?${qString}` : "";
	}

	return qString;
}

/**
 * Formats a request body as needed; if legacy, formats it as a query string.
 * @param {object} body The request body to format.
 * @param {isLegacy} isLegacy Whether this is a legacy request.
 * @returns {(string|FormData)} Response can be any valid request body format.
 */
export function _formatBody(body, isLegacy) {
	if (body instanceof FormData) {
		return body;
	}

	return isLegacy
		? _formatUrlParameters(body)
		: JSON.stringify(body);
}

/**
 * Normalizes an error into a standard format.
 * @param {object} errorResponse The API error. We'll accept just about anything here.
 * @returns {object} An object with an `error` field and a `message` field.
 */
export function _normalizeError(errorResponse) {
	let error = { ...DEFAULT_ERROR };
	let message = DEFAULT_ERROR.error.message;

	if (errorResponse && errorResponse.response) {
		if (errorResponse.response.data) {
			error = errorResponse.response.data;
			message = errorResponse.response.data.message || message;
		}
	} else if (errorResponse && errorResponse.error) {
		error = errorResponse;
		message = errorResponse.error.message || message;
	} else {
		if (errorResponse && errorResponse.message) {
			message = errorResponse.message;
		} else if (typeof errorResponse === "string") {
			message = errorResponse;
		}

		// this means the call failed
		error.requestCanceled = true;
	}

	return {
		error,
		message,
	};
}

/**
 * Throws if there's an error in the response.
 * @param {object} response Formatted API response.
 * @param {object} metadata Metadata about the request/response.
 * @throws {object} An object with error information.
 */
export function _checkForErrors(response, metadata) {
	const isError = !response.ok
		|| response.body === undefined
		|| response.body === null
		|| response.body.error;

	if (isError) {
		const errorResponse = response.body;
		const statusCode = response.status;
		const { path, params } = metadata;
		const { error, message } = _normalizeError(errorResponse, path);

		// Pass error to Sentry, if it's not just a 400, an auth error, or a network error.
		const isUnexpectedError = statusCode !== 400;
		const isNetworkError = NetworkError.isNetworkError(message);
		const isAuthError = AuthError.isAuthError(message);
		const shouldSendToSentry = isUnexpectedError && !isNetworkError && !isAuthError;

		if (shouldSendToSentry) {
			Sentry.withScope((scope) => {
				scope.setExtra("path", path);
				scope.setExtra("params", params);
				scope.setExtra("error", errorResponse);

				const exception = new APIError(path, message);
				Sentry.captureException(exception);
			});
		}

		throw error;
	}
}

/**
 * Makes an API call to OkCupid.
 * @param {object} path Endpoint path. Nests under /1/apitun, unless `legacy` option is specified.
 * @param {object} params Data, metadata (cache/headers/method), callbacks (success/error/complete).
 * @returns {Promise} Rejects/resolves with API response.
 */
function apiRequest(path, params = {}) {
	const method = (params.type || METHODS.GET).toUpperCase();
	const headers = _buildHeaders(params.headers, params.version);

	const shouldSendDataInBody = method === METHODS.PUT || method === METHODS.POST;
	const cache = params.cache === false ? "no-cache" : "default";

	// Explicitly defined for backwards compat (some IE defaults to _not_ same-origin).
	const credentials = isLocalHost
		? "include"
		: "same-origin";

	const body = shouldSendDataInBody
		? _formatBody(params.data, params.isLegacy)
		: undefined;
	const urlParams = !shouldSendDataInBody
		? _formatUrlParameters(params.data, { withQuerySeparator: true })
		: "";

	// Legacy API paths don't live under our API_PREFIX.
	const url = params.isLegacy
		? `${SERVER_URL}${path}${urlParams}`
		: `${SERVER_URL}${API_PREFIX}/${path.replace(/^\//, "")}${urlParams}`;

	const request = {
		method,
		headers,
		body,
		cache,
		credentials,
	};

	return fetch(url, request)
		.then(_formatResponse)
		.then((response) => {
			// Throws if the response is problematic.
			_checkForErrors(response, { path, params });

			if (params.success) {
				params.success(response.body);
			}

			if (params.complete) {
				params.complete(response.body);
			}

			return Promise.resolve(response.body);
		})
		.catch((error) => {
			if (params.error) {
				params.error(error);
			}

			if (params.complete) {
				params.complete(error);
			}

			// Only reject if we didn't get an error handler.
			if (params.error || params.complete) {
				return Promise.resolve(error);
			}

			return Promise.reject(error);
		});
}

export default apiRequest;



// WEBPACK FOOTER //
// ./src/shared/util/OkcApi/internal.js